import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'grommet';
import { localAsset } from '@Helpers/asset';
import { FlexibleSelectDropdown } from '.';
import { StyledSVG, StyledReactTooltip, StyledSortButton } from './StyledButtons';

const TOOLTIP_ID = 'dropdown-sort-tooltip';

const DropdownSort = ({
  sortOptions,
  selectedSortField = null,
  sortDirection,
  onSortFieldChange,
  onSortDirectionToggle,
  iconColor = 'var(--page-input-icon)',
  buttonBg = 'var(--page-input-bg)',
  textColor = 'var(--page-input-text)',
  buttonJustify = null,
  customPad = null,
  ...rest
}) => {
  // Choose icon based on direction
  const iconPath = sortDirection === 'asc'
    ? 'images/sort-input-icon-asc.svg'
    : 'images/sort-input-icon-desc.svg';

  // Button disabled if no sort field selected
  const isSortDisabled = !selectedSortField;

  // Tooltip content
  const tooltipContent = !selectedSortField
    ? 'Select a sort option first'
    : `${sortDirection === 'asc' ? 'Ascending' : 'Descending'} (${sortOptions.find((opt) => opt.api === selectedSortField)?.display || ''})`;

  const iconBoxSize = '1.25rem';

  return (
    <Box>
      <Box direction="row" align="center">
        <StyledSortButton
          plain
          buttonBg={buttonBg}
          disabled={isSortDisabled}
          customRadius="var(--app-radius) 0 0 var(--app-radius)"
          onClick={() => {
            if (!isSortDisabled) onSortDirectionToggle();
          }}
          data-tip
          data-for={TOOLTIP_ID}
          iconBoxSize={iconBoxSize}
        >
          <StyledSVG
            src={localAsset(iconPath)}
            width={iconBoxSize}
            height={iconBoxSize}
            $fillColor={iconColor}
            title={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
          />
        </StyledSortButton>
        <FlexibleSelectDropdown
          textColor={textColor}
          handleItemSelect={(value) => onSortFieldChange(value, sortDirection)}
          flexibleOptions={sortOptions}
          noSelectionLabel="Sort by"
          displayKey="display"
          valueKey="api"
          selectedOption={
            sortOptions.find((opt) => opt.api === selectedSortField)?.display || 'Sort by'
          }
          buttonBg={buttonBg}
          iconColor={iconColor}
          buttonJustify={buttonJustify}
          customPad={customPad}
          customRadius="0 var(--app-radius) var(--app-radius) 0"
          {...rest}
        />
      </Box>
      <StyledReactTooltip
        id={TOOLTIP_ID}
        place="bottom"
        effect="solid"
        bgColor="var(--button-primary-bg)"
        arrowColor="var(--button-primary-bg)"
        getContent={() => tooltipContent}
        className="dropdownSortTip"
      />
    </Box>
  );
};

DropdownSort.propTypes = {
  sortOptions: PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.string.isRequired,
    api: PropTypes.string.isRequired,
  })).isRequired,
  selectedSortField: PropTypes.string,
  sortDirection: PropTypes.oneOf(['asc', 'desc']).isRequired,
  onSortFieldChange: PropTypes.func.isRequired,
  onSortDirectionToggle: PropTypes.func.isRequired,
  iconColor: PropTypes.string,
  buttonBg: PropTypes.string,
  textColor: PropTypes.string,
  buttonJustify: PropTypes.string,
  customPad: PropTypes.string,
};

export default DropdownSort;
